'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CommunityPhoto {
  id: number;
  photographer: string;
  location: string;
  image: string;
  alt: string;
  likes: number;
  caption: string;
  date: string;
}

interface DiveReport {
  id: number;
  diver: string;
  site: string;
  date: string;
  visibility: string;
  temperature: string;
  highlights: string;
  rating: number;
}

interface CommunityShowcaseProps {
  className?: string;
}

const CommunityShowcase = ({ className = '' }: CommunityShowcaseProps) => {
  const [activeTab, setActiveTab] = useState<'photos' | 'reports'>('photos');

  const communityPhotos: CommunityPhoto[] = [
  {
    id: 1,
    photographer: "Maria K.",
    location: "Zenobia Wreck",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_198251908-1772357911982.png",
    alt: 'Diver photographing marine life around Zenobia wreck dive site in Larnaca Cyprus',
    likes: 47,
    caption: "Amazing visibility at Zenobia today! The groupers were very curious about my camera.",
    date: "2 days ago"
  },
  {
    id: 2,
    photographer: "Alex D.",
    location: "Cape Greco Caves",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b04295ef-1772357912848.png",
    alt: 'Diver silhouette at Cape Greco underwater cave entrance in Ayia Napa Cyprus',
    likes: 32,
    caption: "The light effects in the caves were absolutely magical this morning!",
    date: "4 days ago"
  },
  {
    id: 3,
    photographer: "Sophie L.",
    location: "Amphitheatre",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c16d7084-1777019625359.png",
    alt: 'Colorful nudibranch sea slug on coral reef during macro dive in Cyprus Mediterranean',
    likes: 28,
    caption: "Found this beautiful nudibranch during my macro dive. Cyprus marine life never ceases to amaze!",
    date: "1 week ago"
  },
  {
    id: 4,
    photographer: "James R.",
    location: "Akrotiri Peninsula",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_191377a3c-1772357910784.png",
    alt: 'School of barracuda forming circular pattern around diver at Akrotiri Peninsula Cyprus',
    likes: 51,
    caption: "Surrounded by a school of barracuda - what an incredible experience!",
    date: "1 week ago"
  }];


  const recentReports: DiveReport[] = [
  {
    id: 1,
    diver: "Captain Mike",
    site: "Zenobia Wreck",
    date: "30 Jan 2026",
    visibility: "25m+",
    temperature: "19°C",
    highlights: "Large grouper spotted near the stern. Excellent conditions for photography.",
    rating: 5
  },
  {
    id: 2,
    diver: "Elena P.",
    site: "Cape Greco Caves",
    date: "28 Jan 2026",
    visibility: "20m",
    temperature: "18°C",
    highlights: "Perfect conditions for cave diving. Saw octopus and several moray eels.",
    rating: 4
  },
  {
    id: 3,
    diver: "Diving Cyprus",
    site: "Amphitheatre",
    date: "26 Jan 2026",
    visibility: "30m+",
    temperature: "19°C",
    highlights: "Crystal clear water, perfect for beginners. Sea turtle sighting reported.",
    rating: 5
  }];


  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) =>
    <Icon
      key={i}
      name="StarIcon"
      size={16}
      className={i < rating ? "text-warning" : "text-muted"} />

    );
  };

  return (
    <section
      className={`py-12 sm:py-14 md:py-16 ${className}`}
      style={{
        background: '#0B1622',
        borderTop: '1px solid rgba(255,255,255,0.06)'
      }}>
      
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <p
            className="text-sm font-semibold mb-2 uppercase tracking-widest"
            style={{ color: '#FF8A3D' }}
          >
            Real dives. Real conditions.
          </p>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-headline font-bold mb-3 sm:mb-4"
            style={{ color: '#EAF4F8' }}>
            
            Community Showcase
          </h2>
          <p
            className="text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2 sm:px-0"
            style={{ color: '#C8DDE6' }}>
            
            Discover Cyprus underwater world through the eyes of our diving community
          </p>
        </div>

        {/* Tab Navigation — full width on mobile */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div
            className="rounded-xl p-1 w-full sm:w-auto"
            style={{
              background: '#0F2234',
              border: '1px solid rgba(255,255,255,0.06)'
            }}>
            
            <button
              onClick={() => setActiveTab('photos')}
              className="px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 w-1/2 sm:w-auto text-sm sm:text-base min-h-[44px]"
              style={{
                background: activeTab === 'photos' ? '#6FD3E0' : 'transparent',
                color: activeTab === 'photos' ? '#0B1622' : '#C8DDE6'
              }}>
              
              Community Photos
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className="px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 w-1/2 sm:w-auto text-sm sm:text-base min-h-[44px]"
              style={{
                background: activeTab === 'reports' ? '#6FD3E0' : 'transparent',
                color: activeTab === 'reports' ? '#0B1622' : '#C8DDE6'
              }}>
              
              Recent Dive Reports
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'photos' ?
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
            {communityPhotos.map((photo) =>
          <div
            key={photo.id}
            className="rounded-xl overflow-hidden transition-all duration-300"
            style={{
              background: '#0F2234',
              border: '1px solid rgba(255,255,255,0.06)'
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.border = '1px solid rgba(111,211,224,0.2)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.border = '1px solid rgba(255,255,255,0.06)';
            }}>
            
                {/* Photo — shorter on mobile */}
                <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
                  <AppImage
                src={photo.image}
                alt={photo.alt}
                className="w-full h-full object-cover" />

                  <div
                className="absolute top-3 right-3 rounded-full px-2 py-1"
                style={{ background: 'rgba(11,22,34,0.65)', backdropFilter: 'blur(4px)' }}>
                
                    <div className="flex items-center space-x-1">
                      <Icon name="HeartIcon" size={14} style={{ color: '#FF8A3D' }} />
                      <span className="text-xs font-medium" style={{ color: '#EAF4F8' }}>{photo.likes}</span>
                    </div>
                  </div>
                </div>

                {/* Photo Info */}
                <div className="p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm sm:text-base" style={{ color: '#EAF4F8' }}>{photo.photographer}</span>
                    <span className="text-xs" style={{ color: '#C8DDE6' }}>{photo.date}</span>
                  </div>
                  <div className="flex items-center space-x-1 mb-2">
                    <Icon name="MapPinIcon" size={14} style={{ color: '#6FD3E0' }} />
                    <span className="text-sm" style={{ color: '#6FD3E0' }}>{photo.location}</span>
                  </div>
                  <p className="text-xs sm:text-sm line-clamp-2" style={{ color: '#C8DDE6' }}>
                    {photo.caption}
                  </p>
                </div>
              </div>
          )}
          </div> :

        <div className="max-w-4xl mx-auto space-y-4">
            {recentReports.map((report) =>
          <div
            key={report.id}
            className="rounded-xl p-6 transition-all duration-300"
            style={{
              background: '#0F2234',
              border: '1px solid rgba(255,255,255,0.06)'
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.border = '1px solid rgba(111,211,224,0.2)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.border = '1px solid rgba(255,255,255,0.06)';
            }}>
            
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                  <div className="flex items-center space-x-3 mb-2 md:mb-0">
                    <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(111,211,224,0.08)', border: '1px solid rgba(111,211,224,0.2)' }}>
                  
                      <Icon name="UserIcon" size={20} style={{ color: '#6FD3E0' }} />
                    </div>
                    <div>
                      <h3 className="font-medium" style={{ color: '#EAF4F8' }}>{report.diver}</h3>
                      <p className="text-sm" style={{ color: '#C8DDE6' }}>{report.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {renderStars(report.rating)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPinIcon" size={16} style={{ color: '#6FD3E0' }} />
                    <span className="text-sm font-medium" style={{ color: '#EAF4F8' }}>{report.site}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="EyeIcon" size={16} style={{ color: '#6FD3E0' }} />
                    <span className="text-sm" style={{ color: '#C8DDE6' }}>Visibility: {report.visibility}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="FireIcon" size={16} style={{ color: '#FF8A3D' }} />
                    <span className="text-sm" style={{ color: '#C8DDE6' }}>Water: {report.temperature}</span>
                  </div>
                </div>

                <p style={{ color: '#C8DDE6' }}>{report.highlights}</p>
              </div>
          )}
          </div>
        }

        {/* Call to Action */}
        <div className="text-center mt-12">
          <div
            className="rounded-xl p-6 max-w-2xl mx-auto"
            style={{
              background: '#0F2234',
              border: '1px solid rgba(111,211,224,0.15)'
            }}>
            
            {/* Camera / underwater photo icon */}
            <div className="flex justify-center mb-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="44"
                height="44"
                viewBox="0 0 44 44"
                fill="none"
                aria-hidden="true"
              >
                {/* Camera body */}
                <rect x="5" y="14" width="34" height="22" rx="4" fill="none" stroke="#6FD3E0" strokeWidth="2"/>
                {/* Lens */}
                <circle cx="22" cy="25" r="7" fill="none" stroke="#6FD3E0" strokeWidth="2"/>
                <circle cx="22" cy="25" r="3.5" fill="#6FD3E0" fillOpacity="0.25"/>
                {/* Viewfinder bump */}
                <path d="M15 14v-3a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3" stroke="#6FD3E0" strokeWidth="2" strokeLinecap="round"/>
                {/* Flash dot */}
                <circle cx="33" cy="20" r="1.5" fill="#6FD3E0"/>
                {/* Small wave beneath — ocean hint */}
                <path d="M10 39 Q14 37 18 39 Q22 41 26 39 Q30 37 34 39" stroke="#6FD3E0" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
              </svg>
            </div>

            <h3
              className="font-headline font-semibold mb-2"
              style={{ color: '#EAF4F8' }}>
              Share Your Cyprus Diving Experience
            </h3>
            <p className="mb-3" style={{ color: '#C8DDE6' }}>
              Want to share your photos, dive reports, or marine life sightings? We'd love to feature your content. Send us your submissions — photos, dive reports, marine life sightings, or anything else you'd like to share with the community.
            </p>
            <p className="mb-3" style={{ color: '#C8DDE6' }}>
              Email us at{' '}
              <a
                href="mailto:hello@cyprusdiving.info"
                className="font-semibold underline"
                style={{ color: '#6FD3E0' }}
              >
                hello@cyprusdiving.info
              </a>
            </p>
            <p className="text-xs" style={{ color: '#8AABB8' }}>
              By submitting content you agree to our{' '}
              <Link
                href="/privacy-policy"
                className="underline hover:opacity-80 transition-opacity"
                style={{ color: '#6FD3E0' }}
              >
                Privacy Policy
              </Link>
              . We will only use your content as described therein.
            </p>
          </div>
        </div>
      </div>
    </section>);

};

export default CommunityShowcase;